/** Serialized into the existing webview. No cookie transfer or raw auth response. */
export async function loginInPage(input: { origin: string; email: string; password: string | null }) {
  const pageLocation = (globalThis as unknown as { location: { origin: string } }).location;
  const failed = (status: number, error: string) => ({ ok: false, status, error });
  if (pageLocation.origin !== input.origin) return failed(409, 'Die App-Adresse hat sich geändert.');
  const expected = input.email.trim().toLowerCase();
  const identity = async () => {
    let anonymous = false;
    for (const path of ['/api/auth/session', '/api/auth/me']) {
      try {
        const r = await fetch(path, { credentials: 'include', redirect: 'error', signal: AbortSignal.timeout(6000) });
        if (r.status === 401) { anonymous = true; continue; }
        if (!r.ok || !r.headers.get('content-type')?.includes('json')) continue;
        const d = await r.json() as { user?: {email?:string} | null; session?: {user?:{email?:string}}; isAuthenticated?:boolean };
        const email = d.user?.email ?? d.session?.user?.email;
        if (typeof email === 'string' && d.isAuthenticated !== false) return { email: email.trim().toLowerCase() };
        if (d.user === null || d.isAuthenticated === false) anonymous = true;
      } catch { /* A network failure is not proof of a missing login. */ }
    }
    return { anonymous };
  };
  const before = await identity();
  if (before.email === expected) return { ok: true, status: 200, reused: true };
  if (before.email) return failed(409, 'In diesem Profil ist ein anderes Konto angemeldet. Passendes Profil wählen.');
  if (!before.anonymous) return failed(503, 'App-Sitzung nicht prüfbar; keine neue Anmeldung ausgeführt.');
  if (!input.password) return failed(401, 'Keine bestehende Anmeldung und kein freigegebener Zugang hinterlegt.');
  if (pageLocation.origin !== input.origin) return failed(409, 'Die App-Adresse hat sich geändert.');
  try {
    const r = await fetch('/api/auth/login', { method: 'POST', credentials: 'include', redirect: 'error',
      signal: AbortSignal.timeout(15000), headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, password: input.password }) });
    // The response may contain tokens. Neither return nor log it.
    if (!r.ok) return failed(r.status, 'Der freigegebene Zugang wurde von der App nicht angenommen.');
    const after = await identity();
    if (after.email !== expected) return failed(401, 'Login beantwortet, aber die App bestätigt das gewünschte Konto nicht.');
    return { ok: true, status: r.status, reused: false };
  } catch { return failed(503, 'App-Anmeldung nicht erreichbar.'); }
}

export function accountEnvironmentMatches(environment: string, credentialSet: 'dev' | 'production') {
  return environment === (credentialSet === 'production' ? 'produktion' : 'staging');
}

/** Preserve the requested screen and reject cross-origin return redirects. */
export function postLoginTarget(current: string, fallback: string): string {
  const u = new URL(current);
  const requested = u.pathname === '/login'
    ? u.searchParams.get('returnUrl') || u.searchParams.get('next') || fallback
    : u.pathname + u.search + u.hash;
  const target = new URL(requested, u.origin);
  return target.origin === u.origin && ['http:', 'https:'].includes(target.protocol)
    ? target.href : new URL(fallback, u.origin).href;
}
